<template>
  <div class="accordionPlay" data-testid="accordion-play">
    <div>
      <div
        class="accordionPlay__header"
        :class="{ active: defaultExpanded }"
        @click="toggleAccordion"
      > 
        <div class="accordionPlay__container">
          <template v-if="type === 'lesson'">
            <span v-if="lock" class="accordionPlay__header--lesson">
              <IconGoogle name="lock" color="var(--maincolor)" />
            </span>
          </template>
          <template v-else>
            <span
              class="accordionPlay__header--icon"
              :class="{ 'accordion-icon': true, expanded: defaultExpanded }"
            >
              <IconGoogle name="keyboard_arrow_down" color="var(--maincolor)" />
            </span>
            <span v-if="lock" class="accordionPlay__header--lock">
              <IconGoogle name="lock" color="var(--maincolor)" />
            </span>
          </template>
          <span class="accordionPlay__header--title">
            {{ title }}
          </span>
        </div>
        <div v-if="manageCategory && getIsAdmin && emptyStateCategory == 1" class="accordionPlay__manage-category" @click.stop="openCategoryModal">
          <IconGoogle name="edit" size="1rem" />
          <span>{{ t('empty_state.manage_category') }}</span>
        </div>
      </div>
    </div>

    <div v-if="defaultExpanded" class="accordionPlay__content">
      <slot></slot>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { modal } from '@/events'
import { useAuthStore } from '@/store/auth.store'
import { useHomeLegacyStore } from '@/store/home-legacy.store'

const { t } = useI18n()
const authStore = useAuthStore()
const homeLegacyStore = useHomeLegacyStore()

const props = defineProps<{
  title?: string
  lock?: boolean
  type?: string
  item?: object
  defaultExpanded?: boolean
  manageCategory?: boolean
}>()

const emit = defineEmits<{
  toggle: []
}>()

const expanded = ref(false)
const emptyStateCategory = ref<boolean | number>(false)

const getEmptyState = computed(() => homeLegacyStore.getEmptyState)
const getIsAdmin = computed(() => authStore.isAdmin())

watch(() => props.defaultExpanded, (newValue) => {
  expanded.value = newValue === true
})

onMounted(() => {
  if (getEmptyState.value) {
    emptyStateCategory.value = getEmptyState.value.empty_state_category
  }
})

function toggleAccordion() {
  emit('toggle')
}

function openCategoryModal() {
  modal.show('ModalCategoria', {})
}
</script>

<style lang="scss" scoped>
.accordionPlay {
  border-radius: var(--default-radius);

  &__container{
    display: flex;
    gap: 16px;
  }

  &__manage-category{
    display: flex;
    gap: 5px;
    margin-right: 3em;

    span{
      color: #878787;
      text-align: center;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 19.6px;
      white-space: nowrap;
    }

    :deep(.svg-inner path ) {
      fill: #878787;
    }
  }

  &__header {
    border-radius: 8px;
    height: 60px;
    padding: 12px 16px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 16px;
    color: var(--fontcolor);
    background: var(--accordion-header);
    transition: background-color 0.3s ease, color 0.3s ease;
    justify-content: space-between;

    &:hover {
      background: var(--accordion-header-active-hover);
      color: var(--maincolor);

      :deep(.svgCss svg path ) {
        fill: var(--maincolor);
      }
    }

    &:active,
    &.active {
      border-radius: 8px 8px 0 0;
      background: var(--accordion-header-active-hover);
      color: var(--maincolor);
      :deep(.svgCss svg path ) {
        fill: var(--maincolor);
      }
    }

    &--lesson {
        :deep(svg ) {
          width: 18px;
          height: 18px;
        } 
        :deep(svg path ) {
          fill: var(--opposite-color);
        }
      }

    &--lock {
      background: var(--accordion-header-lock);
      padding: 8px;
      border-radius: var(--default-radius);

      :deep(svg path ) {
        fill: var(--accordion-header-lock-icon) !important;
      }

      :deep(svg ) {
        width: 16px;
        height: 16px;
      }

    }

    &--icon {
      :deep(svg ) {
          width: 20px;
          height: 20px;
      }
      :deep(.svgCss svg path ) {
        fill: var(--maincolor);
      }
    }

    span {
      display: flex;
      align-items: center;
    }

    &--title {
      font-weight: bold;
      word-break: break-word;
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box !important;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical; 
    }
  }

  &__content {
    background: var(--accordion-content);
    border-radius: 0 0 8px 8px;
    padding: 16px;

    /* Desktop */
    @media screen and (min-width: 900px) {
      padding: var(--spacing-24) var(--spacing-48);
    }
  }

  .expanded {
    transform: rotate(180deg);
  }
}
</style>
